<template>
  <patient-profile>
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave" />
    <v-dialog
      content-class="patient-snapshot-slider-dialog"
      v-model="isShowImage"
    >
      <patient-snapshot-slider
        v-bind:snapshots="files.current"
        v-bind:value="selectedSnapshot"
        v-on:close="closeSlider"
      />
    </v-dialog>
    <drag-and-drop-patient-snapshot-file
      class="snapshot-list"
      hint="не более 24 Мб, файлы .jpg и .png"
      v-bind:accepted-list="$options.acceptedFileTypeList"
      v-bind:error-files="files.error"
      v-bind:input-files="[...files.current, ...files.uploading]"
      v-bind:violation-causes="inputErrors"
      v-bind:disable-mode-switch="false"
      v-bind:initial-view-mode="$options.galleryMode"
      v-on:download-file="onSnapshotDownload"
      v-on:open-file="onOpenFile"
      v-on:remove-file="onRemoveFile"
      v-on:upload-file="onAddFileToUploaded"
      v-on:click.native="onClickForm"
    />
    <button
      type="button"
      v-if="isUploadingFiles"
      class="snapshot-list__save-button"
      v-on:click="onUploadSnapshots">Загрузить</button>
  </patient-profile>
</template>

<script>
import dateMixin from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import { popoverMixin } from '@/mixins/builtInPopoverMixin';
import { Success } from 'monet';
import PatientProfile from '@/views/ClinicPanel/PatientProfile.vue';
import PatientSnapshotSlider from '@/components/PatientSnapshotSlider.vue';
import { GALLERY_MODE } from '@/components/SwitchViewMode.vue';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import DragAndDropPatientSnapshotFile from '@/components/DragAndDropPatientSnapshotFile.vue';
import metricMixin from '@/mixins/metricMixin';
import { GOAL_UPLOAD_PATIENT_SNAPSHOTS } from '@/plugins/metric';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Разрешенные типы файлов.
 * @type {string[]}
 */
const acceptedFileTypeList = ['image/png', 'image/jpeg', 'image/jpg', '.jpeg', '.jpg', '.png'];

export default {
  acceptedFileTypeList,
  galleryMode: GALLERY_MODE,
  components: {
    DragAndDropPatientSnapshotFile,
    PatientSnapshotSlider,
    PatientProfile,
    ModalDialogPreventLeave,
  },
  computed: {
    /**
     * @description Предотвращать уход со страницы.
     * @returns {boolean}
     */
    needPreventLeave() {
      return this.isUploadingFiles;
    },
    /**
     * @description Идентификатор пациента.
     * @type {number}
     */
    patientId: (vm) => parseInt(vm.$route.params.id, 10),
    /**
     * @description Есть файлы на загрузку.
     * @type {boolean}
     */
    isUploadingFiles: ({ files }) => files.uploading.length > 0,
  },
  created() {
    const onResponse = (files) => {
      this.files.current = [...this.files.current, ...files];
    };
    this.fetchSnapshotList().then(onResponse);
    this.fetchVisitsImageList().then(onResponse);
  },
  data() {
    return {
      /**
       * @description Выбранный для просмотра снимок.
       * @type {Number}
       */
      selectedSnapshot: 0,
      /**
       * @description Заголовок окна просмотра изображения.
       * @type {string}
       */
      dialogTitle: '',
      /**
       * @description Изображение.
       * @type {any}
       */
      imageSource: null,
      /**
       * @description Список файлов.
       * @type {any}
       */
      files: {
        /**
         * @description Текущий список файлов.
         * @type {any[]}
         */
        current: [],
        /**
         * @description Список файлов на удаление.
         * @type {any[]}
         */
        deleting: [],
        /**
         * @description Список файлов на загрузку.
         * @type {any[]}
         */
        uploading: [],
        /**
         * @description Список файлов загрузка которых не удалась.
         * @type {any[]}
         */
        error: [],
      },
      /**
       * @description Нарушение ограничений.
       * @type {any}
       */
      inputErrors: {},
      /**
       * @description Ожидание окончания запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Показывать просмотр изображения.
       * @type {boolean}
       */
      isShowImage: false,
    };
  },
  maxTextLength: 20000,
  methods: {
    /**
     * @description Обработка события клика на форму
     * @returns {void}
     */
    onClickForm() {
      this.reachGoalIfNotReach(GOAL_UPLOAD_PATIENT_SNAPSHOTS);
    },
    /**
     * @description Закрыть слайдер.
     * @returns {void}
     */
    closeSlider() {
      this.isShowImage = false;
    },
    /**
     * @description Получить список снимков пациента.
     * @returns {any[]}
     */
    async fetchSnapshotList() {
      const onFail = (error) => {
        const title = error.getTitle();
        if (title) {
          this.notifyFail(title);
        }
        return [];
      };
      const onSuccess = (result) => result;
      const either = await this.$patientGateway.viewSnapshotList(this.patientId);
      return either.fold(onFail, onSuccess);
    },
    /**
     * @description Получить список изображений из файлов визитов пациента.
     * @returns {any[]}
     */
    async fetchVisitsImageList() {
      const onFail = (error) => {
        const title = error.getTitle();
        if (title) {
          this.notifyFail(title);
        }
        return [];
      };
      const onSuccess = (result) => result;
      const either = await this.$patientGateway.viewVisitsImageList(this.patientId);
      return either.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать добавление файла в список на удаление.
     * @param {any} file Файл.
     * @returns {void}
     */
    onAddFileToDeleted(file) {
      if (file.id) {
        this.files.deleting.push(file.id);
      } else {
        this.files.uploading = this.files.uploading.filter((item) => item !== file);
      }
    },
    /**
     * @description Обработать добавление файла в список на загрузку.
     * @param {File} file Файл.
     */
    onAddFileToUploaded(file) {
      this.files.uploading.push(file);
    },
    /**
     * @description Открыть снимок для предпросмотра.
     * @param {number}                       patientId Идентификатор пациента.
     * @param {File|{id:number,name:string}} file      Снимок.
     * @returns {void}
     */
    openSnapshotPreview(patientId, file) {
      if (file.id) {
        this.dialogTitle = file.name;
        return this.$patientGateway.downloadSnapshot(patientId, file.id);
      }
      this.dialogTitle = file.name;
      return Success(file);
    },
    /**
     * @description Открыть снимок визита для предпросмотра.
     * @param {number}                                      patientId Идентификатор пациента.
     * @param {File|{id:number,visitId:number,name:string}} file      Снимок визита.
     * @returns {void}
     */
    openVisitSnapshotPreview(file) {
      if (file.id) {
        this.dialogTitle = file.name;
        return this.$visitGateway.downloadFile(file.visitId, file.id);
      }
      this.dialogTitle = file.name;
      return Success(file);
    },
    /**
     * @description Открыть файл для предпросмотра.
     * @param {File|{id:number,name:string}} file
     * @returns {void}
     */
    async onOpenFile(file) {
      const index = this.files.current.findIndex((el) => el.id === file.id);
      if (index === -1) {
        this.notifyFail('Для начала загрузите снимок');
        return;
      }
      this.selectedSnapshot = index;
      this.isShowImage = true;
    },
    /**
     * @description Скачать снимок.
     * @param {number}                       patientId Идентификатор пациента.
     * @param {File|{id:number,name:string}} file      Файл.
     * @returns {Promise<Maybe>}
     */
    async downloadSnapshot(patientId, file) {
      if (file.id) {
        const result = await this.$patientGateway.downloadSnapshot(patientId, file.id);
        return result;
      }
      return Success(file);
    },
    /**
     * @description Скачать снимок визита.
     * @param {File|{id:number,name:string}} file Файл.
     * @returns {Promise<Maybe>}
     */
    async downloadVisitSnapshot(file) {
      if (file.id) {
        const result = await this.$visitGateway.downloadFile(file.visitId, file.id);
        return result;
      }
      return Success(file);
    },
    /**
     * @description Обработать скачивание файла.
     * @param {any} file
     * @returns {void}
     */
    async onSnapshotDownload(file) {
      const onFail = (error) => {
        if (error.title) {
          this.notifyFail(error.title);
        }
      };
      const onSuccess = (response) => {
        saveFile(response, file.name);
      };
      const result = await (
        file.visitId
          ? this.downloadVisitSnapshot(file)
          : this.downloadSnapshot(this.patientId, file)
      );
      result.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать сохранение снимков.
     * @returns {Promise}
     */
    async onUploadSnapshots() {
      this.isAwaitRequest = true;
      const form = new FormData();
      for (let i = 0; i < this.files.uploading.length; i += 1) {
        form.append(`file[${i}]`, this.files.uploading[i]);
      }
      const either = await this.$patientGateway.uploadSnapshots(this.patientId, form);
      either.forEach(({ files, message }) => {
        this.files.current.push(...files);
        this.files.uploading = [];
        this.notifySuccess(message);
      });
      either.forEachFail((error) => {
        const failurePairs = error.causes.map((cause) => [cause.source, cause.details]);
        if (failurePairs.length === 0 && error.title) {
          this.notifyFail(error.title);
        }
        this.inputErrors = Object.fromEntries(failurePairs) || {};
        this.files.error = this.files.uploading.slice();
      });
      this.isAwaitRequest = false;
      return either;
    },
    /**
     * @description Обработать удаление файла.
     * @param {any} file
     * @returns {void}
     */
    async onRemoveFile(file) {
      const dialog = await this.showRemoveDialog('Вы действительно хотите удалить снимок?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ message }) => {
        this.notifySuccess(message);
        this.files.current = this.files.current.filter((item) => item !== file);
      };
      const onFail = (error) => {
        if (error.title) {
          this.notifyFail(error.title);
        }
      };
      if (file.id) {
        const result = await (
          file.visitId
            ? this.$visitGateway.deleteFiles(file.visitId, [file.id])
            : this.$patientGateway.deleteSnapshots(this.patientId, file.id)
        );
        result.fold(onFail, onSuccess);
      } else {
        this.files.uploading = this.files.uploading.filter((item) => item !== file);
      }
    },
    /**
     * @description сохранить и уйти
     * @return {void}
     */
    saveAndLeave() {
      this
        .onUploadSnapshots()
        .then((result) => { result.forEach(this.actionAfterClosingDialogLeaveConfirm); })
        .finally(() => { this.isShownDialogLeaveConfirm = false; });
    },
  },
  mixins: [
    builtInDialogMixin,
    dateMixin,
    notifierMixin,
    popoverMixin,
    preventLeaveWithoutConfirmMixin,
    metricMixin,
  ],
  name: 'ClinicPanelPatientSnapshots',
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";
$switch-width: 16px;

::v-deep .patient-snapshot-slider-dialog {
  width: 90%;
}

.show-image {
  display: flex;
  flex-direction: column;
  height: 90vh;
  &__container {
    position: relative;
    flex: 1;
    img {
      position: absolute;
      height: 100%;
      object-fit: contain;
      width: 100%;
    }
  }
}

.snapshot-list {
  margin-top: 10px;
  &__save-button {
    margin-top: 10px;
    @include buttons.btn-blue(100px, 30px, 16px);
  }
}

@media screen and (max-width: 480px) {
  ::v-deep .patient-snapshot-slider-dialog {
    width: 100%;
    margin: 0;
  }
}
</style>
